import { initializeHttpWrapper as initialize } from '@pendo/http';
import { getActiveUser, getActiveSubscription } from '@/data/session/session-globals';
import { clearFlags as clearLocalFlags } from '@/data/flags/local-flags';
import throttle from 'lodash/throttle';
import { fetchUser, logout } from '@/vuex/modules/auth/auth.utils.js';

export function encodeValueIfNotUrlSafe (val) {
    const decodedVal = decodeURIComponent(val);

    // value is already url safe
    if (decodedVal === val) return val;

    return b64EncodeUnicode(decodedVal);
}

export function b64EncodeUnicode (str) {
    const encodedString = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) =>
        String.fromCharCode(parseInt(p1, 16)),
    );

    return btoa(encodedString)
        .replace('+', '-')
        .replace('/', '_');
}

export function toUrlSafeBase64 (str) {
    return Buffer.from(str)
        .toString('base64')
        .replace(/\+/g, '-')
        .replace(/\//g, '_');
}

export function replaceSNAME (url, subscriptionName) {
    return url.replace(/_SNAME_/, subscriptionName);
}

export function handleRequest (request) {
    if (request?.url === '/logout' && request?.method === 'post') {
        clearLocalFlags();
    }

    return request;
}

export function handleResponse (response) {
    if (!response || response.status === 204) {
        return Promise.resolve();
    }

    return response;
}

// We can't directly import our router instance without breaking a ton of unit tests elsewhere in the app,
// so as a workaround we'll match against public paths
const publicRouteMatchStrings = [
    '/register',
    '/login',
    '/forgot',
    '/invitation/confirm',
    '/register/complete',
    '/register/resetpassword',
    '/resetpassword',
    '/invalid-invitation-data',
];

export function isNotOnPublicPage () {
    return !publicRouteMatchStrings.some((startingPath) =>
        window.location.pathname.startsWith(startingPath),
    );
}

export const throttledUserCheck = throttle(() => fetchUser(), 2000, { trailing: false });

export function conditionallyLogOutUserOnError (error) {
    const isForbiddenError = error.response && error.response.status === 401;
    const isUserInitRequest = error.config.url.startsWith('/api/init');
    const isNotOnRootPage = window.location.pathname !== '/';

    const shouldForceLogOut = isForbiddenError && isUserInitRequest && isNotOnPublicPage() && isNotOnRootPage;

    if (shouldForceLogOut) {
        logout();

        const queryParamString = generateLoginQueryParams().toString();

        window.location.href = `/login?${queryParamString}`;
    } else if (isForbiddenError && !isUserInitRequest && isNotOnPublicPage()) {
        throttledUserCheck();
    }
}

export function generateLoginQueryParams () {
    const existingSearch = new URLSearchParams(window.location.search);
    const newSearch = new URLSearchParams();

    if (existingSearch.get('after')) {
        newSearch.set('after', existingSearch.get('after'));
    } else {
        const { href, origin } = window.location;
        const after = href.replace(origin, '');
        newSearch.set('after', after);
    }

    if (existingSearch.get('version')) {
        newSearch.set('version', existingSearch.get('version'));
    }

    return newSearch;
}

export function handleResponseErrors (error) {
    conditionallyLogOutUserOnError(error);

    return Promise.reject(error);
}

/* Set up config and hooks for using @pendo/http wrapper:
    import {http} from '@pendo/http';
    http.get('https://app.pendo.io/api/_SID_/user/_UID_');
*/
export function initializeHttpWrapper () {
    const http = initialize({
        forceOverride: true,
        retryCount: 3,
        retryDelay: 500,
        getUserId: () => getActiveUser()?.id || null,
        getSubscriptionId: () => getActiveSubscription()?.id || null,
        getSubscriptionName: () => getActiveSubscription()?.name || null,
        interceptResponseExtensionHandler: handleResponse,
        interceptErrorExtensionHandler: handleResponseErrors,
        interceptRequestExtensionHandler: handleRequest,
    });

    return http;
}
